'use client'

import { alert } from '@repo/ui/Alert'
import { confirm } from '@repo/ui/Confirm'
import { DateSelection } from '@repo/ui/DateSelection'
import { getFormSchemas } from './formSchema'
import { parseDate } from '@repo/utils/time'
import { SaveIcon } from '@repo/ui/SaveIcon'
import { SelectMenu } from '@repo/ui/SelectMenu'
import { TextEntry } from '@repo/ui/TextEntry'
import { useContractWorkFormStore } from '@repo/store/useContractWorkFormStore'
import { useDebounce } from '@uidotdev/usehooks'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { useUrlStore } from '@repo/store/useUrlStore'
import { WarningIcon } from '@repo/ui/WarningIcon'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

const ContractWorkFormsEditForm = ({ type = 'CDSI' }) => {
  const { queryParams } = useUrlStore()
  const [formData, setFormData] = useState({})
  const [isEditMode, setIsEditMode] = useState(false)
  const [pageTitle, setPageTitle] = useState('')
  const [fields, setFields] = useState([])
  const [hasCheckedForValues, setHasCheckedForValues] = useState(false)
  const { matters } = useMattersStore()
  const [activeDate, setActiveDate] = useState(new Date())
  const [clientNo, setClientNo] = useState(null)
  const [matterNo, setMatterNo] = useState(null)
  const [uniqueFileNo, setUniqueFileNo] = useState(null)
  const [branchId, setBranchId] = useState(null)
  const [showLaaCodes, setShowLaaCodes] = useState(true)

  // vars for unique checks
  const debouncedFormData = useDebounce(formData, 400)
  const [showMaatIdError, setShowMaatIdError] = useState(false)
  const [showDsccNumberError, setShowDsccNumberError] = useState(false)

  const navigate = useNavigate()
  const {
    fetchLaaCodes,
    fetchCrmInvestigationProceeding,
    saveCrmInvestigationProceeding,
    fetchCrmPrisonLawAppealsReview,
    saveCrmPrisonLawAppealsReview,
    checkForExistingMaatId,
    checkForExistingDsccNumber
  } = useContractWorkFormStore()

  useEffect(() => {
    const fetchData = async () => {
      setIsEditMode(false)

      const { client, matter } = queryParams || {}
      if (!client) return

      setClientNo(client)
      setMatterNo(matter)

      const { uniqueFileNo, branchId } = matters[`${client}-${matter}`] || {}
      setUniqueFileNo(uniqueFileNo || null)
      setBranchId(branchId || null)

      try {
        const args = { clientNo: client, matterNo: matter }
        let data = null

        switch (type.toUpperCase()) {
          case 'CDSI':
          case 'CDSPR':
            data = await fetchCrmInvestigationProceeding(args)
            break
          case 'CDSAR':
          case 'CDSPL':
            data = await fetchCrmPrisonLawAppealsReview(args)
            break
        }

        if (!data) throw 'No data'

        // the data exists, so that means we're in edit mode
        setIsEditMode(true)
        setFormData(data)
      } catch (error) {
        console.error(error)
        // this just means the data doesn't exist yet, so that means we're in create mode
        setIsEditMode(false)
      } finally {
        setHasCheckedForValues(true)
      }
    }

    // don't fetch this until we've loaded the schema (which means pageTitle will be set), and don't fetch it more than once!
    if (pageTitle && !hasCheckedForValues && queryParams) {
      fetchData()
    }
  }, [
    type,
    queryParams,
    fetchCrmInvestigationProceeding,
    fetchCrmPrisonLawAppealsReview,
    hasCheckedForValues,
    pageTitle,
    matters
  ])

  useEffect(() => {
    const loadFormSchema = async () => {
      const formSchemas = await getFormSchemas(showLaaCodes)
      const schema = formSchemas[type.toUpperCase()]
      if (!schema) return

      if (!showLaaCodes) {
        schema.fields = schema.fields.filter((field) => field.id !== 'LAA_CODE')
      }

      const { pageTitle, fields } = schema

      setPageTitle(pageTitle)
      setFields(fields)
    }

    loadFormSchema()
  }, [type, showLaaCodes])

  useEffect(() => {
    const loadLaCodes = async () => {
      if (!branchId || !activeDate) return

      let feeClass = ''
      switch (type.toUpperCase()) {
        case 'CDSI':
          feeClass = 'INVEST'
          break
        case 'CDSPR':
          feeClass = 'REPRES'
          break
        case 'CDSAR':
          feeClass = 'APPEAL'
          break
        case 'CDSPL':
          feeClass = 'PRISON'
          break
      }

      if (!feeClass) return

      // don't bother fetching codes before 1 May 2025, since they don't exist
      if (!activeDate || activeDate < new Date('2025-05-01')) {
        useContractWorkFormStore.setState({ laaCodes: [] })

        // reset the LAA codes to null if they exist in the form data
        const { investigationLaaFeeCodeId, proceedingLaaFeeCodeId, appealReviewLaaFeeCodeId, prisonLawLaaFeeCodeId } =
          formData
        let changes = {}
        if (investigationLaaFeeCodeId) changes.investigationLaaFeeCodeId = null
        if (proceedingLaaFeeCodeId) changes.proceedingLaaFeeCodeId = null
        if (appealReviewLaaFeeCodeId) changes.appealReviewLaaFeeCodeId = null
        if (prisonLawLaaFeeCodeId) changes.prisonLawLaaFeeCodeId = null

        if (Object.keys(changes).length) setFormData((prev) => ({ ...prev, ...changes }))
        return
      }

      fetchLaaCodes({ activeDate: activeDate.toISOString().split('T')[0], type, branchId, feeClass })
    }

    loadLaCodes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, fetchLaaCodes, matters, activeDate, branchId, showLaaCodes])

  useEffect(() => {
    const calculateActiveDate = async () => {
      // we send an 'activeDate' param to the API. This should be based on the matter UFN date, or the proceeding representation order date if it exists (in the Proceedings form)
      // finally, we should default to the current date if no other date is available

      // uniqueFileNo looks like this: 230125/001, i.e, DDMMYY/NN
      // we need to convert this to a date we can send to the API
      let activeDate = uniqueFileNo?.split('/')?.[0] || null
      activeDate = activeDate ? parseDate(activeDate, 'ddMMyy', new Date()) : null

      if (type.toUpperCase() === 'CDSPR' && formData?.proceedingRepresentationOrderDate) {
        activeDate = new Date(formData.proceedingRepresentationOrderDate)
      }

      activeDate = activeDate || new Date()

      // prevent BST issues by setting time to midday
      activeDate.setHours(12)

      setActiveDate(activeDate)

      // don't show LAA codes before 1 May 2025, since they don't exist
      setShowLaaCodes(activeDate >= new Date('2025-05-01'))
    }

    calculateActiveDate()
  }, [matters, clientNo, matterNo, uniqueFileNo, type, formData, showLaaCodes])

  const goBack = () => {
    navigate(`/case-management/matters/contract-work-forms?client=&page=${type}`)
  }

  const updateFormData = ({ id, value }) => {
    const changes = { [id]: value }

    // if the item changing is LAA code, prefill form data based on the LAA Code selection
    if (id.includes('LaaFeeCodeId')) {
      const { stageReached, standardFeeCode, standardFeeSubCategory, isYouthCourt, isDutySolicitor } = value

      const formDataKeys = Object.keys(formData)

      let prefix = ''
      switch (type.toUpperCase()) {
        case 'CDSI':
          prefix = 'investigation'
          break
        case 'CDSPR':
          prefix = 'proceeding'
          break
        case 'CDSAR':
          prefix = 'appealReview'
          break
        case 'CDSPL':
          prefix = 'prisonLaw'
          break
      }

      const stageReachedKey = formDataKeys.find((key) => key === `${prefix}StageReached`)
      if (stageReachedKey) {
        changes[stageReachedKey] = stageReached || null
      }

      const standardFeeCodeKey = formDataKeys.find((key) => key === `${prefix}StandardFee`)
      if (standardFeeCodeKey) {
        changes[standardFeeCodeKey] = standardFeeCode || null
      }

      const standardFeeSubCategoryKey = formDataKeys.find((key) => key === `${prefix}Category1SubCategory`)
      if (standardFeeSubCategoryKey) {
        changes[standardFeeSubCategoryKey] = standardFeeSubCategory || null
      }

      const youthCourtKey = formDataKeys.find((key) => key === `${prefix}YouthCourt`)
      if (youthCourtKey) {
        changes[youthCourtKey] = isYouthCourt === true ? 'Yes' : isYouthCourt === false ? 'No' : null
      }

      const dutySolicitorKey = formDataKeys.find((key) => key === `${prefix}DutySolicitor`)
      if (dutySolicitorKey) {
        changes[dutySolicitorKey] = isDutySolicitor === true ? 'Yes' : isDutySolicitor === false ? 'No' : null
      }
    }

    if (Object.keys(changes).length) setFormData((prev) => ({ ...prev, ...changes }))
  }

  useEffect(() => {
    if (type.toUpperCase() !== 'CDSPR') return

    const checkForExistingMaatIdOnChange= async (newValue) => {
      const existingMaatId = await checkForExistingMaatId({ clientNo, matterNo, proceedingMaatId: newValue })
      setShowMaatIdError(existingMaatId)
    }

    const newValue = debouncedFormData.proceedingMaatId
    if (newValue) checkForExistingMaatIdOnChange(newValue)
  }, [clientNo, matterNo, debouncedFormData?.proceedingMaatId, checkForExistingMaatId, type])

  useEffect(() => {
    if (type.toUpperCase() !== 'CDSI') return

    const checkForExistingDsccNumberOnChange= async (newValue) => {
      const existingDsccNumber = await checkForExistingDsccNumber({ clientNo, matterNo, investigationDsccNumber: newValue })
      setShowDsccNumberError(existingDsccNumber)
    }

    const newValue = debouncedFormData.investigationDsccNumber
    if (newValue) checkForExistingDsccNumberOnChange(newValue)
  }, [clientNo, matterNo, debouncedFormData?.investigationDsccNumber, checkForExistingDsccNumber, type])

  const handleCancel = async (e) => {
    e.preventDefault()
    const proceed = await confirm('Are you sure you want to cancel? No data will be saved.')
    if (proceed) goBack()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData || !queryParams?.client) {
      alert('No data to save. Please add some data first.')
      throw 'No data to save'
    }

    try {
      const payload = { clientNo: queryParams.client, matterNo: queryParams.matter }

      // flatten any object values to a single level
      Object.keys(formData).forEach((key) => {
        const value = formData[key]
        if (!value) return

        if (typeof value === 'string') {
          payload[key] = value
        } else if (typeof value === 'object' && typeof value.toISOString === 'function') {
          // this is a date
          // set the time to midday to avoid any BST issues
          value.setHours(12)
          payload[key] = value.toISOString().split('T')[0]
        } else if (typeof value === 'object' && value.value) {
          // this is a list item
          payload[key] = value.value
        }
      })

      console.table(payload)

      const args = { payload, method: isEditMode ? 'PUT' : 'POST' }

      switch (type.toUpperCase()) {
        case 'CDSI':
        case 'CDSPR':
          await saveCrmInvestigationProceeding(args)
          break
        case 'CDSAR':
        case 'CDSPL':
          await saveCrmPrisonLawAppealsReview(args)
          break
      }

      goBack()
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ paddingBottom: '40px' }}>
      <MUIStack
        direction="column"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={'30px'}
        sx={{
          width: 'calc(100% - 80px)',
          maxWidth: '1520px',
          margin: '0 auto',
          marginTop: '20px',
          padding: '30px',
          height: `auto`,
          minHeight: '500px',
          backgroundColor: 'white',
          borderRadius: '6px',
          border: '1px solid #D8DCE5',
          position: 'relative'
        }}
      >
        <MUIStack direction="row" justifyContent="space-between" gap={2} sx={{ width: '100%' }}>
          <MUIStack direction="column" spacing={1} alignItems="flex-start">
            <MUITypography variant="h5" sx={{ fontSize: '1rem', cursor: 'pointer', userSelect: 'none' }}>
              Contract work forms
            </MUITypography>
            <MUITypography
              variant="body2"
              sx={{
                borderRadius: 1,
                padding: '4px 8px',
                background: '#E7EBF2',
                display: 'inline',
                cursor: 'pointer',
                userSelect: 'none',
                textTransform: 'uppercase'
              }}
            >
              {pageTitle}
            </MUITypography>
          </MUIStack>

          <FormButtons handleCancel={handleCancel} />
        </MUIStack>

        <MUIStack
          sx={{
            margin: '0 auto',
            width: '100%',
            backgroundColor: '#DFD1B655',
            padding: '30px 20px',
            borderRadius: '6px',
            flex: 1
          }}
        >
          <MUIStack
            direction="row"
            flexWrap="wrap"
            gap={8}
            alignItems="flex-start"
            sx={{
              width: '100%',
              maxWidth: '752px',
              flex: 1
            }}
          >
            {hasCheckedForValues &&
              fields?.map((field) => (
                <FormField
                  key={field.id}
                  field={field}
                  onChange={updateFormData}
                  value={formData[field.apiPropertyName] || ''}
                />
              ))}

              {showMaatIdError && (
                <UniqueCheckError message={`Please note - a proceeding with this MAAT ID already exists on another matter.`} />
              )}

              {showDsccNumberError && (
                <UniqueCheckError message={`Please note - an investigation with this DSCC Number already exists on another matter.`} />
              )}
          </MUIStack>
        </MUIStack>

        <FormButtons handleCancel={handleCancel} />
      </MUIStack>
    </form>
  )
}

export default ContractWorkFormsEditForm

ContractWorkFormsEditForm.propTypes = {
  type: PropTypes.string
}

const UniqueCheckError = ({ message }) => {
  return (
    <MUIStack direction="row" alignItems="center" gap={4} sx={{ padding: 4, borderRadius: 1, backgroundColor: 'warning.opacity', width: '100%' }}>
      <WarningIcon sx={{ width: '32px', height: '32px', color: 'white' }} />
      <MUITypography variant="body1" sx={{ mb: 0, color: 'white' }}>{message}</MUITypography>
    </MUIStack>
  )
}

UniqueCheckError.propTypes = {
  message: PropTypes.string
}

const FormButtons = ({ handleCancel, preventSave }) => {
  return (
    <MUIStack direction="row" gap={2} sx={{ alignSelf: 'flex-end' }}>
      <MUIButton variant="text" onClick={handleCancel}>
        Cancel
      </MUIButton>

      <MUIButton
        variant="contained"
        type="submit"
        disabled={preventSave}
        sx={{ backgroundColor: 'success.light', '&:hover': { backgroundColor: 'success.lightHover' } }}
      >
        <SaveIcon sx={{ color: preventSave ? '#999' : 'white', width: '20px', marginRight: '10px' }} />
        Save
      </MUIButton>
    </MUIStack>
  )
}

FormButtons.propTypes = {
  handleCancel: PropTypes.func,
  preventSave: PropTypes.bool
}

const FormField = ({ field, onChange, value, validationErrors }) => {
  const { id, type, description, length, readOnly, apiPropertyName } = field || {}
  const [items, setItems] = useState([])
  const { fetchCrmListItems, laaCodes } = useContractWorkFormStore()
  const [fetchBegun, setFetchBegun] = useState(false)

  const handleChange = (value) => {
    onChange({ value, id: apiPropertyName })
  }

  const setListItemFromListStringValueOrDefaultValue = useCallback(
    (items) => {
      items.forEach((item) => {
        if (value) {
          // if the value is a flattened string, set it to the full object
          const firstWordOfItemValue = typeof item.value === 'string' ? item.value.split(' ')[0] : item.value
          if (item.value === value || firstWordOfItemValue === value) {
            onChange({ value: item, id: apiPropertyName })
          }
        } else if (item.isDefault) {
          // if the value is not set, and the item is the default, set it
          onChange({ value: item, id: apiPropertyName })
        }
      })
    },
    [value, onChange, apiPropertyName]
  )

  const setLaaItemFromLaaId = useCallback(() => {
    laaCodes.forEach((item) => {
      if (value) {
        // if the value is a flattened string, set it to the full object
        if (item.laaFeeCodeId === value) {
          onChange({ value: item, id: apiPropertyName })
        }
      }
    })
  }, [value, onChange, apiPropertyName, laaCodes])

  useEffect(() => {
    const fetchItems = async () => {
      let listItems = []

      if (fetchBegun) {
        listItems = items
      } else {
        setFetchBegun(true)
        listItems = await fetchCrmListItems({ fieldId: id })

        setItems(listItems)

        listItems.forEach((item) => {
          item.apiPropertyName = apiPropertyName
        })
      }

      setListItemFromListStringValueOrDefaultValue(listItems)
    }

    if (type === 'list') {
      fetchItems()
    }

    if (type === 'laa') {
      setLaaItemFromLaaId()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id, fetchCrmListItems, apiPropertyName, fetchBegun, value, onChange])

  if (type === 'list') {
    return (
      <SelectMenu
        label={description}
        labelKey="value"
        idKey="apiPropertyName"
        valueKey="value"
        optionRenderValue={(item) => item.value}
        items={items}
        value={value || ''}
        onChange={handleChange}
        disabled={readOnly}
        sx={{ width: '100%', maxWidth: '360px' }}
        searchable={items && items.length > 5}
      />
    )
  }

  if (type === 'laa') {
    return (
      <SelectMenu
        label={description}
        labelKey="feeCode"
        idKey="apiPropertyName"
        valueKey="laaFeeCodeId"
        optionRenderValue={(item) => item.feeCode}
        items={laaCodes}
        value={value || ''}
        onChange={handleChange}
        disabled={readOnly}
        sx={{ width: '100%', maxWidth: '360px' }}
        searchable={laaCodes && laaCodes.length > 5}
      />
    )
  }

  if (type === 'date') {
    return (
      <DateSelection
        format="EEE d MMM yyyy"
        value={value ? new Date(value) : null}
        onChange={handleChange}
        label={description}
        showAdornment={true}
        sx={{ width: '100%', maxWidth: '360px' }}
        size={'small'}
        enableTypedDdMmYyyy={true}
      />
    )
  }

  if (type === 'string') {
    let helperTextArray = []
    if (length) {
      helperTextArray.push(`Max ${length} characters`)
      if (value) helperTextArray.push(`Characters: ${value.length} / ${length}`)
    }

    return (
      <TextEntry
        label={description}
        value={value || ''}
        onChange={handleChange}
        size={'small'}
        name={id}
        errors={validationErrors ? { [name]: validationErrors } : null}
        showErrorInLabel={false}
        limit={length || 1000}
        readOnly={readOnly}
        helperText={helperTextArray.join(', ')}
        sx={{
          width: '100%',
          maxWidth: '360px',
          backgroundColor: 'white',
          borderRadius: '4px',
          height: type === 'Multiline' ? 'auto' : '40px',
          'input, textarea, .MuiInputBase-multiline, .MuiInputBase-root': { backgroundColor: 'white' },
          '&.MuiTextField-root': { backgroundColor: 'transparent !important' },
          '.MuiFormHelperText-root': {
            ml: 0,
            color: validationErrors ? 'error.dark' : 'text.primary',
            backgroundColor: 'transparent'
          },
          mb: helperTextArray.length > 0 ? 4 : 0
        }}
      />
    )
  }
}

FormField.propTypes = {
  field: PropTypes.object,
  onChange: PropTypes.func,
  value: PropTypes.any,
  validationErrors: PropTypes.array
}
